// Tempo and beat phase from an onset-strength envelope (see onset.ts).
//
// 1. Autocorrelate the envelope over beat periods for 60-180 BPM, weighted
//    toward ~120 BPM so a song is not read at half or double speed.
// 2. Refine the winning period to a fraction of a frame.
// 3. Slide a comb of that period over the recent envelope to find where the
//    beats land; recent beats count more so the estimate follows the music.

export const DETECT_MIN_BPM = 60;
export const DETECT_MAX_BPM = 180;

export interface TempoEstimate {
  bpm: number;
  // 0-1: how strongly periodic the envelope is at that tempo.
  confidence: number;
  // RMS of the detrended onset envelope: how much percussive change there is
  // at all. A steady tone can look perfectly periodic but has almost none.
  strength: number;
  // Time of the most recent beat, in the same clock as endTime.
  lastBeatTime: number;
}

// flux: envelope frames, oldest first. frameRate: frames per second.
// endTime: time of the last frame.
export function estimateTempo(
  flux: ArrayLike<number>,
  frameRate: number,
  endTime: number,
): TempoEstimate | null {
  const n = flux.length;
  if (n < frameRate * 4) return null;

  // Remove the slow-moving level (~0.25 s average) and keep the rises.
  const prefix = new Float64Array(n + 1);
  for (let i = 0; i < n; i++) prefix[i + 1] = prefix[i]! + flux[i]!;
  const half = Math.max(1, Math.round(frameRate * 0.125));
  const e = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const lo = Math.max(0, i - half);
    const hi = Math.min(n, i + half + 1);
    e[i] = Math.max(0, flux[i]! - (prefix[hi]! - prefix[lo]!) / (hi - lo));
  }

  let ac0 = 0;
  for (let i = 0; i < n; i++) ac0 += e[i]! * e[i]!;
  ac0 /= n;
  if (ac0 <= 0) return null;

  const minLag = Math.floor((60 * frameRate) / DETECT_MAX_BPM);
  const maxLag = Math.ceil((60 * frameRate) / DETECT_MIN_BPM);
  const ac = new Float64Array(maxLag + 2);
  for (let lag = minLag - 1; lag <= maxLag + 1; lag++) {
    let sum = 0;
    for (let i = 0; i + lag < n; i++) sum += e[i]! * e[i + lag]!;
    ac[lag] = sum / (n - lag);
  }

  let best = -1;
  let bestScore = -Infinity;
  let mean = 0;
  for (let lag = minLag; lag <= maxLag; lag++) {
    mean += ac[lag]!;
    const score = ac[lag]! * tempoPrior((60 * frameRate) / lag);
    if (score > bestScore) {
      bestScore = score;
      best = lag;
    }
  }
  mean /= maxLag - minLag + 1;

  // Parabolic interpolation around the peak for a fractional period.
  const a = ac[best - 1]!;
  const b = ac[best]!;
  const c = ac[best + 1]!;
  const denom = a - 2 * b + c;
  const shift = denom < 0 ? Math.max(-0.5, Math.min(0.5, (0.5 * (a - c)) / denom)) : 0;
  const period = best + shift;
  const bpm = (60 * frameRate) / period;
  const confidence = Math.max(0, Math.min(1, (b - mean) / (ac0 - mean)));

  // Beat phase: frames back from the newest frame to the most recent beat.
  let bestPhase = 0;
  let bestPhaseScore = -Infinity;
  for (let phase = 0; phase < period; phase += 0.5) {
    let score = 0;
    let weight = 1;
    for (let pos = n - 1 - phase; pos >= 1; pos -= period) {
      score += weight * interpolate(e, pos);
      weight *= 0.85;
    }
    if (score > bestPhaseScore) {
      bestPhaseScore = score;
      bestPhase = phase;
    }
  }

  return {
    bpm,
    confidence,
    strength: Math.sqrt(ac0),
    lastBeatTime: endTime - bestPhase / frameRate,
  };
}

function tempoPrior(bpm: number): number {
  const octaves = Math.log2(bpm / 120);
  return Math.exp(-0.5 * (octaves / 0.9) ** 2);
}

function interpolate(e: Float64Array, pos: number): number {
  const i = Math.floor(pos);
  const f = pos - i;
  return e[i]! * (1 - f) + (e[i + 1] ?? e[i]!) * f;
}
